import { PlacedPiece, GameEngine, PieceType, DEFAULT_CONFIG } from '@game/engine';
import { TileInfo } from './TileInfo';

interface ShrinkPreview {
  dimension: 'width' | 'height';
  affectedPieces: { piece: PlacedPiece; refund: number }[];
  pieceRefund: number;
  expansionRefund: number;
  totalRefund: number;
}

interface PieceSidebarProps {
  piece: PlacedPiece | null;
  allPieces: PlacedPiece[];
  isPinned?: boolean;
  onDelete?: () => void;
  onUpgrade?: (pieceId: string) => void;
  gameEngine?: GameEngine;
  tax?: number;
  currency?: number;
  prestigeLevel?: number;
  hoveredSelectorType?: PieceType | null;
  shrinkPreview?: ShrinkPreview | null;
}

export function PieceSidebar({ piece, isPinned, onDelete, onUpgrade, gameEngine, tax, currency = 0, prestigeLevel = 1, hoveredSelectorType, shrinkPreview }: PieceSidebarProps) {
  const enhancementInfo = piece && gameEngine ? (() => {
    const multiplier = gameEngine.getPieceEnhancementMultiplier(piece);
    return multiplier > 0 ? { multiplier, enhancerCount: 0 } : undefined;
  })() : undefined;

  if (shrinkPreview) {
    const label = shrinkPreview.dimension === 'width' ? 'last 2 columns' : 'last 2 rows';
    return (
      <div style={{ fontSize: '13px' }}>
        <div style={{ fontWeight: 600, marginBottom: '8px', color: '#b02828' }}>Sell {label}</div>
        <div style={{ color: '#555', marginBottom: '12px', lineHeight: 1.5 }}>
          The {label} will be removed.{shrinkPreview.affectedPieces.length > 0 && ' Pieces in that area will be deleted.'}
        </div>
        {shrinkPreview.affectedPieces.length > 0 && (() => {
          const groups = shrinkPreview.affectedPieces.reduce<Record<string, { count: number; totalRefund: number }>>((acc, { piece: p, refund }) => {
            if (!acc[p.type]) acc[p.type] = { count: 0, totalRefund: 0 };
            acc[p.type].count++;
            acc[p.type].totalRefund += refund;
            return acc;
          }, {});
          return (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ color: '#888', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Removed pieces</div>
              {Object.entries(groups).map(([type, { count, totalRefund }]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#444' }}>
                  <span>{count}× {type} piece</span>
                  <span style={{ color: '#27ae60' }}>+{totalRefund}</span>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{ borderTop: '1px solid #eee', paddingTop: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555', marginBottom: '4px' }}>
            <span>Expansion refund</span>
            <span style={{ color: '#27ae60' }}>+{shrinkPreview.expansionRefund}</span>
          </div>
          {shrinkPreview.affectedPieces.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555', marginBottom: '4px' }}>
              <span>Piece refunds</span>
              <span style={{ color: '#27ae60' }}>+{shrinkPreview.pieceRefund}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: '6px', fontSize: '14px' }}>
            <span>Total gain</span>
            <span style={{ color: '#27ae60' }}>+{shrinkPreview.totalRefund}</span>
          </div>
        </div>
      </div>
    );
  }

  if (piece) {
    const pieceConfig = DEFAULT_CONFIG.tileConfigs[piece.type];
    const upgradeCosts = pieceConfig.upgradeCosts ?? [];
    const nextLevel = piece.level + 1;
    const upgradeCost = upgradeCosts[piece.level - 1];
    const atMaxPrestigeLevel = piece.level >= prestigeLevel;
    const atAbsoluteMaxLevel = piece.level >= 8;
    const canUpgrade = !atMaxPrestigeLevel && !atAbsoluteMaxLevel && upgradeCost !== undefined;
    const canAffordUpgrade = canUpgrade && currency >= upgradeCost;

    return (
      <div style={{ fontSize: '13px' }}>
        <TileInfo pieceType={piece.type} piece={piece} enhancementInfo={enhancementInfo} tax={tax} />
        {isPinned && (
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {!atAbsoluteMaxLevel && (
              <button
                onClick={() => onUpgrade?.(piece.id)}
                disabled={atMaxPrestigeLevel || !canAffordUpgrade}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: (!atMaxPrestigeLevel && canAffordUpgrade) ? '#7c3aed' : '#c4b5fd',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!atMaxPrestigeLevel && canAffordUpgrade) ? 'pointer' : 'not-allowed',
                  fontWeight: 500,
                  fontSize: '13px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => { if (!atMaxPrestigeLevel && canAffordUpgrade) e.currentTarget.style.backgroundColor = '#6d28d9'; }}
                onMouseLeave={(e) => { if (!atMaxPrestigeLevel && canAffordUpgrade) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
              >
                {atMaxPrestigeLevel ? 'Upgrade (Max level)' : `Upgrade ($${upgradeCost})`}
              </button>
            )}
            <button
              onClick={onDelete}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#ff6b6b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '13px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ff5252'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b6b'}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  if (hoveredSelectorType) {
    return (
      <div style={{ fontSize: '13px' }}>
        <TileInfo pieceType={hoveredSelectorType} tax={tax} />
      </div>
    );
  }

  return (
    <div style={{ color: '#999', padding: '20px', fontSize: '13px' }}>
      Select a piece to see details
    </div>
  );
}
